import { DialogOptions, useAuth, useDialog, useSnack } from '@/logic/state';

import AdBlockDialog from '../stand-alone/ad-block-dialog';
import { ApplyMultiVoucherDialog } from '../checkout/apply-multi-voucher-dialog';
import { ApplyOfferDialog } from '../offers/apply-offer-dialog';
import { ChangeShowTimesDialog } from '../movie-finder/show-times/change-show-times-dialog';
import { ClearAllFiltersDialog } from '../movie-finder/filters/clear-all-filters-dialog';
import { ClearHistoryConfirmDialog } from '../movie-finder/clear-history-confirm-dialog';
import { DeleteOfferConfirmDialog } from '../offers/delete-offer-confirm-dialog';
import { DeleteVoucherConfirmDialog } from '../checkout/delete-voucher-confirm-dialog';
import { ExperienceInfoDialog } from '../experience/experience-info-dialog';

import NotificationsPopoverContent from '../navbar/notifications/notifications-popover-content';
import OfflineDialog from '../stand-alone/offline-dialog';
import ReportMuviTeamDialog from '@/features/login-and-signup/modals/contact-us-dialog';
import dynamic from 'next/dynamic';
import shallow from 'zustand/shallow';

import { FC, useState, useCallback, useEffect } from 'react';
import { CardNotSameAppliedConfirmDialog } from '../checkout/card-not-same-applied-confirm-dialog';
import { CardNotSameVerifiedConfirmDialog } from '../checkout/card-not-same-verified-confirm-dialog';
import { CardNotSupportedConfirmDialog } from '../checkout/card-not-supported-confirm-dialog';
import { GuestOtpForm } from '../checkout/guest-booking/guest-otp-form';
import { OfferLimitConfirmDialog } from '../checkout/offer-limit-confirm-dialog';
import { OfferLimitInfoDialog } from '../checkout/offer-limit-info-dialog';
import { ChangeCityConfirmDialog } from '../home/change-city-confirm-dialog';
import { CitiesForm } from '../home/cities-form';
import { NotAvailableOfferInfoDialog } from '../offers/dialogs/info-dialogs/not-available-offer-info-dialog';
import { AddCreditCardForm } from '../profile/add-credit-card-form/add-credit-card-form';
import { DeleteCardConfirmationDialog } from '../profile/delete-card-confirmation-dialog';
import { EditCreditCardForm } from '../profile/edit-credit-card-from';
import { OtpForm } from '../register-and-login/otp-form';
import { TermsAndConditionsForm } from '../register-and-login/terms-and-conditions';
import { SnackbarPopup } from '../shared/snackbar-popup';
import { LogoutConfirmDialog } from '../side-menu/logout-confirm-dialog';

import { CancelSeatsSelectionConfirmDialog } from '../booking/dialogs/confirm-dialogs/cancel-seats-selection-confirm-dialog';
import { ChangeExperienceConfirmDialog } from '../booking/dialogs/confirm-dialogs/change-experience-confirm-dialog';
import { ChangeShowtimeConfirmDialog } from '../booking/dialogs/confirm-dialogs/change-showtime-confirm-dialog';
import { CompanionConfirmDialog } from '../booking/dialogs/confirm-dialogs/companion-confirm-dialog';
import { WheelChairConfirmDialog } from '../booking/dialogs/confirm-dialogs/wheel-chair-confirm-dialog';
import { HouseRules } from '../booking/dialogs/house-rules';
import { ReservedSeatsInfoDialog } from '../booking/dialogs/info-dialogs/reserved-seats-info-dialog';
import { SessionExpiredInfoDialog } from '../booking/dialogs/info-dialogs/session-expired-info-dialog';
import { RulesOfEntryDialog } from '../booking/dialogs/rules-of-entry-dialog';
import { ShareSeatsPlanDialog } from '../booking/dialogs/share-seats-plan-dialog';
import LogoutTimerDialog from '../stand-alone/logout-timer-dialog';
import { useLogoutTimer } from '@/logic/hooks/use-logout-timer';
import { useBraze } from '@/logic/hooks/use-braze';

const MainDialog = dynamic(
  () => import('../shared/main-dialog').then(component => component.MainDialog),
  { ssr: false }
);

const DimmedLayer = dynamic(
  () => import('../stand-alone/dimmed-layer').then(component => component.DimmedLayer),
  { ssr: false }
);

const ShowTimesDrawer = dynamic(
  () =>
    import('../stand-alone/show-times-drawer').then(
      component => component.ShowTimesDrawer
    ),
  { ssr: false }
);

const CompleteProfileForm = dynamic(
  () =>
    import('../../features/login-and-signup/modals/complete-profile-form').then(
      component => component.CompleteProfileForm
    ),
  { ssr: false }
);

const OldUserChangeEmailForm = dynamic(
  () => import('@/features/login-and-signup/modals/old-user/old-user-change-email-form'),
  { ssr: false }
);
const OldUserForm = dynamic(
  () => import('@/features/login-and-signup/modals/old-user/old-user-form'),
  { ssr: false }
);
const OldUserEmailOtpForm = dynamic(
  () => import('@/features/login-and-signup/modals/old-user/old-user-email-otp-form'),
  { ssr: false }
);
const OldUserChangeEmailOtpForm = dynamic(
  () =>
    import('@/features/login-and-signup/modals/old-user/old-user-change-email-otp-form'),
  { ssr: false }
);
const OtpEmailForm = dynamic(
  () =>
    import('../../features/login-and-signup/modals/otp-email-form').then(
      component => component.OtpEmailForm
    ),
  { ssr: false }
);
const AvatarsForm = dynamic(
  () =>
    import('../../features/login-and-signup/modals/avatars-form').then(
      component => component.AvatarsForm
    ),
  { ssr: false }
);
const PhoneNumberForm = dynamic(
  () =>
    import('../../features/login-and-signup/modals/phone-number-form').then(
      component => component.PhoneNumberForm
    ),
  { ssr: false }
);
const EditProfileForm = dynamic(
  () =>
    import('../../features/login-and-signup/modals/edit-profile-form').then(
      component => component.EditProfileForm
    ),
  { ssr: false }
);
const ChangeEmailOtpForm = dynamic(
  () =>
    import(
      '../../features/login-and-signup/modals/change-email-and-phone-number/change-email-otp-form'
    ).then(component => component.ChangeEmailOtpForm),
  { ssr: false }
);
const ChangeEmailResetCodeForm = dynamic(
  () =>
    import(
      '../../features/login-and-signup/modals/change-email-and-phone-number/change-email-reset-code-form'
    ).then(component => component.ChangeEmailResetCodeForm),
  { ssr: false }
);
const ChangePhoneNumberOtpForm = dynamic(
  () =>
    import(
      '../../features/login-and-signup/modals/change-email-and-phone-number/change-phone-number-otp-form'
    ).then(component => component.ChangePhoneNumberOtpForm),
  { ssr: false }
);
const ChangePhoneNumberResetCodeForm = dynamic(
  () =>
    import(
      '../../features/login-and-signup/modals/change-email-and-phone-number/change-phone-number-reset-code-form'
    ).then(component => component.ChangePhoneNumberResetCodeForm),
  { ssr: false }
);
const NewEmailForm = dynamic(
  () =>
    import(
      '../../features/login-and-signup/modals/change-email-and-phone-number/new-email-form'
    ).then(component => component.NewEmailForm),
  { ssr: false }
);
const NewPhoneNumberForm = dynamic(
  () =>
    import(
      '../../features/login-and-signup/modals/change-email-and-phone-number/new-phone-number-form'
    ).then(component => component.NewPhoneNumberForm),
  { ssr: false }
);
const ProfileChangeOptionsForm = dynamic(
  () =>
    import(
      '../../features/login-and-signup/modals/change-email-and-phone-number/profile-change-options-form'
    ).then(component => component.ProfileChangeOptionsForm),
  { ssr: false }
);

const WalletExpiryDetailsDialog = dynamic(
  () =>
    import('../../features/wallet/modals/wallet-expiry-details-dialog').then(
      component => component.default
    ),
  { ssr: false }
);

const AboutWalletDialog = dynamic(
  () =>
    import('../../features/wallet/modals/about-wallet-dialog').then(
      component => component.default
    ),
  { ssr: false }
);

const AboutCashbackDialog = dynamic(
  () =>
    import('../../features/cashback/modals/about-cashback-dialog').then(
      component => component.default
    ),
  { ssr: false }
);

const AddTopUpDialog = dynamic(
  () =>
    import('../../features/wallet/modals/add-top-up-dialog').then(
      component => component.AddTopUpDialog
    ),
  { ssr: false }
);

const WalletRemainDialog = dynamic(
  () =>
    import('../../features/wallet/modals/wallet-remain-dialog').then(
      component => component.WalletRemainDialog
    ),
  { ssr: false }
);

const CashbackRemainDialog = dynamic(
  () =>
    import('../../features/cashback/modals/wallet-remain-dialog').then(
      component => component.CashbackRemainDialog
    ),
  { ssr: false }
);

const CancelTicketConfirmDialog = dynamic(
  () =>
    import('../../features/wallet/modals/cancel-ticket-confirm-dialog').then(
      component => component.CancelTicketConfirmDialog
    ),
  { ssr: false }
);

const WalletOtpEmailForm = dynamic(
  () =>
    import('../../features/wallet/modals/wallet-otp-email-form').then(
      component => component.WalletOtpEmailForm
    ),
  { ssr: false }
);

const ChangeEmailConfirmDialog = dynamic(
  () =>
    import('../../features/wallet/modals/change-email-confirm-dialog').then(
      component => component.ChangeEmailConfirmDialog
    ),
  { ssr: false }
);

const StcVerificationCodeModal = dynamic(
  () =>
    import('../../features/stc-pay/modals/stc-verification-code-modal').then(
      component => component.StcVerificationCodeModal
    ),
  { ssr: false }
);

export const Umbrella: FC = () => {
  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const [snack, setSnack] = useSnack(s => [s.snack, s.setSnack], shallow);
  const [mounted, setMounted] = useState(false);
  const user = useAuth(s => s.user, shallow);
  useBraze(); // TODO check later maybe we can move it to layout
  /**
   * It returns a JSX.Element based on the boolean values passed to it
   * @param  - isPhoneNumberForm
   * @returns A JSX.Element
   */
  const getDialog = ({
    isPhoneNumberForm,
    isApplyOfferDialog,
    isTermsAndConditionsForm,
    isOtpForm,
    isCompleteProfileForm,
    isEmailOtpForm,
    isAvatarForm,
    isOldUserForm,
    isProfileChangeOptionsForm,
    isChangeEmailOtpForm,
    isOldUserChangeEmailForm,
    isOldUserEmailOtpForm,
    isOldUserChangeEmailOtpForm,
    isNewEmailForm,
    isChangeEmailResetCodeForm,
    isChangePhoneNumberResetCodeForm,
    isNewPhoneNumberForm,
    isChangePhoneNumberOtpForm,
    isCitiesForm,
    isChangeCityConfirmDialog,
    isRulesOfEntryDialog,
    isHouseRulesDialog,
    isCancelSeatsSelectionConfirmDialog,
    isChangeExperienceConfirmDialog,
    isChangeShowtimeConfirmDialog,
    isWheelChairConfirmDialog,
    isCompanionConfirmDialog,
    isShareSeatsPlanDialog,
    isReservedSeatsInfoDialog,
    isSessionExpiredInfoDialog,
    isAllowedToChangeShowTimesDialog,
    isAllowedToClearAllFiltersDialog,
    isEditProfileForm,
    isDeleteCardConfirmationDialog,
    isDeleteVoucherConfirmDialog,
    isApplyMultiVoucherDialog,
    isAddCreditCardForm,
    isEditCreditCardForm,
    isNotificationPopover,
    isClearHistoryConfirmDialog,
    isNotAvailableOfferInfoDialog,
    isLogoutConfirmDialog,
    isDeleteOfferConfirmDialog,
    isCardNotSameVerifiedConfirmDialog,
    isCardNotSupportedConfirmDialog,
    isCardNotSameAppliedConfirmDialog,
    isOfflineDialog,
    isExperienceInfoDialog,
    isAdBlockDialog,
    isGuestOtpForm,
    isOfferLimitInfoDialog,
    isOfferLimitConfirmDialog,
    isReportMuviDialog,
    isWalletExpiryDetailsDialog,
    isAboutWalletDialog,
    isAboutCashbackDialog,
    isAddTopUpDialog,
    isWalletRemainDialog,
    isCashbackRemainDialog,
    isCancelTicketConfirmDialog,
    isWalletOtpEmailForm,
    isChangeEmailConfirmDialog,
    isStcVerificationCode,
    isLogoutTimerDialog,
  }: Partial<DialogOptions>): JSX.Element => {
    if (isPhoneNumberForm) return <PhoneNumberForm />;
    if (isApplyOfferDialog) return <ApplyOfferDialog />;
    if (isTermsAndConditionsForm) return <TermsAndConditionsForm />;
    if (isOtpForm) return <OtpForm />;
    if (isCompleteProfileForm) return <CompleteProfileForm />;
    if (isEmailOtpForm) return <OtpEmailForm />;
    if (isEditProfileForm) return <EditProfileForm />;
    if (isAvatarForm) return <AvatarsForm />;
    if (isOldUserForm) return <OldUserForm />;
    if (isProfileChangeOptionsForm) return <ProfileChangeOptionsForm />;
    if (isChangeEmailOtpForm) return <ChangeEmailOtpForm />;
    if (isOldUserChangeEmailForm) return <OldUserChangeEmailForm />;
    if (isOldUserEmailOtpForm) return <OldUserEmailOtpForm />;
    if (isOldUserChangeEmailOtpForm) return <OldUserChangeEmailOtpForm />;
    if (isNewEmailForm) return <NewEmailForm />;
    if (isChangeEmailResetCodeForm) return <ChangeEmailResetCodeForm />;
    if (isChangePhoneNumberResetCodeForm) return <ChangePhoneNumberResetCodeForm />;
    if (isNewPhoneNumberForm) return <NewPhoneNumberForm />;
    if (isChangePhoneNumberOtpForm) return <ChangePhoneNumberOtpForm />;
    if (isCitiesForm) return <CitiesForm />;
    if (isChangeCityConfirmDialog) return <ChangeCityConfirmDialog />;
    if (isRulesOfEntryDialog) return <RulesOfEntryDialog />;
    if (isHouseRulesDialog) return <HouseRules />;
    if (isCancelSeatsSelectionConfirmDialog) return <CancelSeatsSelectionConfirmDialog />;
    if (isChangeExperienceConfirmDialog) return <ChangeExperienceConfirmDialog />;
    if (isChangeShowtimeConfirmDialog) return <ChangeShowtimeConfirmDialog />;
    if (isWheelChairConfirmDialog) return <WheelChairConfirmDialog />;
    if (isCompanionConfirmDialog) return <CompanionConfirmDialog />;
    if (isShareSeatsPlanDialog) return <ShareSeatsPlanDialog />;
    if (isReservedSeatsInfoDialog) return <ReservedSeatsInfoDialog />;
    if (isSessionExpiredInfoDialog) return <SessionExpiredInfoDialog />;
    if (isAllowedToChangeShowTimesDialog) return <ChangeShowTimesDialog />;
    if (isAllowedToClearAllFiltersDialog) return <ClearAllFiltersDialog />;
    if (isDeleteCardConfirmationDialog) return <DeleteCardConfirmationDialog />;
    if (isDeleteVoucherConfirmDialog) return <DeleteVoucherConfirmDialog />;
    if (isApplyMultiVoucherDialog) return <ApplyMultiVoucherDialog />;
    if (isAddCreditCardForm) return <AddCreditCardForm />;
    if (isEditCreditCardForm) return <EditCreditCardForm />;
    if (isNotificationPopover) return <NotificationsPopoverContent />;
    if (isClearHistoryConfirmDialog) return <ClearHistoryConfirmDialog />;
    if (isNotAvailableOfferInfoDialog) return <NotAvailableOfferInfoDialog />;
    if (isLogoutConfirmDialog) return <LogoutConfirmDialog />;
    if (isDeleteOfferConfirmDialog) return <DeleteOfferConfirmDialog />;
    if (isCardNotSameVerifiedConfirmDialog) return <CardNotSameVerifiedConfirmDialog />;
    if (isCardNotSupportedConfirmDialog) return <CardNotSupportedConfirmDialog />;
    if (isCardNotSameAppliedConfirmDialog) return <CardNotSameAppliedConfirmDialog />;
    if (isOfflineDialog) return <OfflineDialog />;
    if (isExperienceInfoDialog) return <ExperienceInfoDialog />;
    if (isAdBlockDialog) return <AdBlockDialog />;
    if (isGuestOtpForm) return <GuestOtpForm />;
    if (isOfferLimitInfoDialog) return <OfferLimitInfoDialog />;
    if (isOfferLimitConfirmDialog) return <OfferLimitConfirmDialog />;
    if (isReportMuviDialog) return <ReportMuviTeamDialog />;
    if (isWalletExpiryDetailsDialog) return <WalletExpiryDetailsDialog />;
    if (isAboutWalletDialog) return <AboutWalletDialog />;
    if (isAboutCashbackDialog) return <AboutCashbackDialog />;
    if (isAddTopUpDialog) return <AddTopUpDialog />;
    if (isWalletRemainDialog) return <WalletRemainDialog />;
    if (isCashbackRemainDialog) return <CashbackRemainDialog />;
    if (isCancelTicketConfirmDialog) return <CancelTicketConfirmDialog />;
    if (isWalletOtpEmailForm) return <WalletOtpEmailForm />;
    if (isChangeEmailConfirmDialog) return <ChangeEmailConfirmDialog />;
    if (isStcVerificationCode) return <StcVerificationCodeModal />;
    if (isLogoutTimerDialog) return <LogoutTimerDialog />;
    return <></>;
  };

  const handleCloseSnack = useCallback(() => {
    setSnack(snack => ({
      ...snack,
      isOpen: false,
    }));
  }, [setSnack]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { isPromptVisible } = useLogoutTimer();

  useEffect(() => {
    if (!!user?.token && !!isPromptVisible) {
      setDialog({
        wrapper: true,
        isLogoutTimerDialog: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, user, isPromptVisible]);

  return (
    !!mounted && (
      <>
        <MainDialog>{getDialog({ ...dialog })}</MainDialog>
        <SnackbarPopup {...snack} closeSnack={() => handleCloseSnack()} />
        <ShowTimesDrawer />
        <DimmedLayer />
      </>
    )
  );
};
